import classNames from "classnames";
import DOMPurify from "dompurify";
import React, { type RefObject } from "react";
import { Svg, SvgSize } from "../../../../Shared/Assets/Svg/React";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType
} from "../../../../Shared/Buttons/React";
import { Modal } from "../../../../Shared/Modal/React";
import {
  SimpleInfoAlert,
  SimpleInfoAlertType
} from "../../../../Shared/SimpleInfoAlert/React";
import { useTranslationContext } from "../../context";

interface ProvetTreatmentModalProps {
  active: boolean;
  title: string;
  contentHtml: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  scrollIndicatorRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  modalRef?: RefObject<HTMLDivElement>;
  showSpeciesIncompatibleWarning?: boolean;
}

/**
 * Modal that displays treatment instructions before allowing the user to proceed.
 * Shows detailed information about the selected treatment with a scrollable content area.
 */
export const ProvetTreatmentModal = ({
  active,
  title,
  contentHtml,
  isLoading,
  onConfirm,
  onCancel,
  scrollIndicatorRef,
  contentRef,
  modalRef,
  showSpeciesIncompatibleWarning = false
}: ProvetTreatmentModalProps) => {
  const translationContext = useTranslationContext();
  const translations = translationContext?.translations;
  const titleId = "provet-treatment-modal-title";

  return (
    <Modal
      active={active}
      onClose={onCancel}
      hasFooter={true}
      ref={modalRef}
      titleId={titleId}
      modalName="treatment"
    >
      {/* Modal Header */}
      <div
        ref={contentRef}
        data-modal-scrollable-content
        className="flex flex-col gap-2xl w-full p-xs text-t-neutral-800"
      >
        {/* Species Incompatibility Warning Alert Box */}
        {showSpeciesIncompatibleWarning && (
          <SimpleInfoAlert
            type={SimpleInfoAlertType.Info}
            text={translations?.speciesIncompatibleWarningMessage}
          />
        )}

        <div className="flex flex-col gap-md">
          <h2
            id={titleId}
            className="flex items-center font-guardian text-dark-blue text-t-md sm:text-t-xl font-t-semi-bold leading-lg sm:leading-xxl gap-xs md:gap-sm"
          >
            <Svg href="/dist/sprite.svg#sys-info" additionalClass="fill-t-neutral-700" />
            {translations?.treatmentModalTitle} - {title}
          </h2>
        </div>
      </div>
      {/* Modal Content */}
      <div
        className={classNames(
          "text-body text-t-neutral-800 font-normal !leading-[1.75] md:text-t-md [&_a]:text-inherit",
          "[&_a]:font-t-bold [&_a]:underline [&_a]:decoration-turquoise-800 [&_a]:underline-offset-[0.35rem]",
          "[&_p]:mb-md [&_p]:last:mb-0 [&_ul]:mb-md [&_ul]:last:mb-0 [&_ol]:mb-md [&_ol]:last:mb-0",
          "[&_ul]:list-none [&_ul_li]:relative [&_ul_li]:pl-md [&_ul_li]:mb-sm [&_ul_li:last-child]:mb-0",
          "[&_ul_li::before]:content-[''] [&_ul_li::before]:absolute [&_ul_li::before]:left-0 [&_ul_li::before]:top-[0.6em]",
          "[&_ul_li::before]:w-[1px] [&_ul_li::before]:h-[1px] [&_ul_li::before]:bg-dark-blue [&_ul_li::before]:rounded-full",
          "[&_ol]:list-none [&_ol]:counter-reset-[list-counter] [&_ol_li]:relative [&_ol_li]:pl-md [&_ol_li]:mb-sm",
          "[&_ol_li:last-child]:mb-0 [&_ol_li]:counter-increment-[list-counter] [&_ol_li::before]:content-[counter(list-counter)]",
          "[&_ol_li::before]:absolute [&_ol_li::before]:left-0 [&_ol_li::before]:top-0 [&_ol_li::before]:flex [&_ol_li::before]:items-center",
          "[&_ol_li::before]:justify-center [&_ol_li::before]:w-5 [&_ol_li::before]:h-5 [&_ol_li::before]:text-t-xs",
          "[&_ol_li::before]:font-normal [&_ol_li::before]:text-dark-blue [&_ol_li::before]:bg-white",
          "[&_ol_li::before]:border [&_ol_li::before]:border-dark-blue [&_ol_li::before]:rounded-full"
        )}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentHtml) }}
      />
      {/* Footer with Scroll Indicator and Action Buttons */}
      <div className="flex flex-col sticky z-[2] justify-center bottom-0">
        {/* Scroll Indicator */}
        <div
          ref={scrollIndicatorRef}
          data-scroll-indicator
          className="flex justify-center items-center h-[6.25rem] w-full bg-gradient-to-t from-white to-transparent via-white/90 transition-all duration-300 ease-in-out opacity-100"
        >
          <div className="flex h-[3.125rem] w-[3.125rem] justify-center items-center border-2 border-dark-blue rounded-full overflow-hidden">
            <Svg
              href="/svg/system/arrow-down.svg"
              title={translations?.scrollDown}
              size={SvgSize.LG}
              additionalClass={"fill-dark-blue animate-bounce"}
            />
          </div>
        </div>
        {/* Action Buttons */}
        <div className="py-lg bg-white border-t border-neutral-200">
          <div className="flex flex-col gap-md sm:flex-row">
            <Button
              type={ButtonType.Button}
              size={ButtonSize.LG}
              color={ButtonColor.Green}
              disabled={isLoading}
              isLoading={isLoading}
              onClick={onConfirm}
              data-online-booking-action="confirm"
            >
              {translations?.confirmUnderstand}
            </Button>
            <Button
              type={ButtonType.Button}
              size={ButtonSize.LG}
              color={ButtonColor.OutlineNeutral}
              onClick={onCancel}
              disabled={isLoading}
              data-online-booking-action="cancel"
            >
              {translations?.changeTreatment}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
